import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { HilPresentationRequestEnvelope } from '@bizfirst/hil-types';
import type { HilChatMessage, HilChatMessageStatus } from '../types/HilChatMessage';

/**
 * One already-superseded turn's system message(s), captured just before a NEW active envelope
 * replaced it (see `captureSwap` below), plus how many of the user's own echoed replies existed
 * at that moment — needed to interleave it correctly against `echoed`, since a re-suspending
 * node type (e.g. Octopus's AI Agent chat) reuses the same Presentation.Order on every turn (so
 * `order`-based sorting alone can't distinguish turns).
 */
export interface HilChatSystemHistoryEntry {
  message: HilChatMessage;
  echoedBoundary: number;
}

export interface HilChatTranscriptState {
  /** EngageSessionID (Reader-equivalent) this buffer currently belongs to. `null` before any session has been seen. */
  sessionID: number | null;
  currentStep: number;
  currentFormData: Record<string, unknown>;
  /** The client-local echo of the user's own sent turns — the reader has no visibility into submitted reply data. */
  echoed: HilChatMessage[];
  /** Buffer of past agent/system turns for HIL node types that re-suspend on the SAME session across multiple turns. */
  systemHistory: HilChatSystemHistoryEntry[];
  echoIDCounter: number;
  historyIDCounter: number;

  /** Bookkeeping for `captureSwap` below — not meant to be read directly by consumers. */
  lastSeenEnvelope: HilPresentationRequestEnvelope | null;
  lastSeenVisible: HilChatMessage[];

  /**
   * Resets every transcript buffer whenever the ACTUAL active session changes (a genuinely new
   * conversation) — safe to call unconditionally on every render/mount of HilChatWindow, since
   * this store — not the calling component's own lifecycle — is the source of truth for "did the
   * session actually change". Calling it repeatedly with the same sessionID (e.g. immediately
   * after HilChatWindow remounts because the user switched back to the "HIL Actions" Observe tab)
   * is a deliberate no-op. That no-op is exactly what lets the full transcript (echoed replies +
   * captured past-turn history) survive the component unmounting and remounting: this store is a
   * module singleton (same pattern as `@bizfirst/hil-react`'s `useHilStore`), so it is never torn
   * down when a consuming component unmounts.
   */
  ensureSession: (sessionID: number | null) => void;
  setCurrentStep: (step: number) => void;
  setCurrentFormData: (data: Record<string, unknown>) => void;
  addEcho: (message: HilChatMessage) => void;
  updateEchoStatus: (id: string, status: HilChatMessageStatus) => void;
  /** Allocates a fresh, session-scoped, monotonically increasing local echo id (`${sessionID}-uN`). */
  allocateEchoID: () => string;
  /**
   * Detects whether the active envelope reference has genuinely changed since the last time this
   * was called for the CURRENT session — and, if so, archives the previous turn's visible system
   * message(s) into `systemHistory` (each stamped with a fresh history id) before recording the
   * new envelope as "last seen". Deliberately callable on every render (idempotent no-op when
   * nothing changed) — the same capture-on-swap approach the original component-local
   * implementation used (see DevelopmentHistoryLog.md), just backed by store state instead of
   * component refs so it keeps working correctly across the component unmounting mid-turn (e.g.
   * the user navigates to another Observe tab while a reply POST is still in flight — see
   * CHAT_RESOLVE_GRACE_MS in hilStore.ts for why that can legitimately take up to ~60s).
   * `ensureSession` resets `lastSeenEnvelope` back to `null` on a genuine session change, which
   * makes this correctly no-op on the first call for a brand-new session.
   */
  captureSwap: (
    activeEnvelope: HilPresentationRequestEnvelope | null,
    visibleSystemMessages: HilChatMessage[]
  ) => void;
}

// Explicit return type -- without it, TS2742 fires in any consumer outside this package's own
// pnpm workspace (e.g. app-player, chatdesk): the inferred type can't be named without a
// reference to immer's own node_modules path, which isn't portable across workspace boundaries.
export const useHilChatTranscriptStore: UseBoundStore<StoreApi<HilChatTranscriptState>> = create<HilChatTranscriptState>()(
  immer((set, get) => ({
    sessionID: null,
    currentStep: 1,
    currentFormData: {},
    echoed: [],
    systemHistory: [],
    echoIDCounter: 0,
    historyIDCounter: 0,
    lastSeenEnvelope: null,
    lastSeenVisible: [],

    ensureSession: (sessionID) =>
      set((state) => {
        if (state.sessionID === sessionID) return;
        state.sessionID = sessionID;
        state.currentStep = 1;
        state.currentFormData = {};
        state.echoed = [];
        state.systemHistory = [];
        state.echoIDCounter = 0;
        state.historyIDCounter = 0;
        state.lastSeenEnvelope = null;
        state.lastSeenVisible = [];
      }),

    setCurrentStep: (step) =>
      set((state) => {
        state.currentStep = step;
      }),

    setCurrentFormData: (data) =>
      set((state) => {
        state.currentFormData = data;
      }),

    addEcho: (message) =>
      set((state) => {
        state.echoed.push(message);
      }),

    updateEchoStatus: (id, status) =>
      set((state) => {
        const target = state.echoed.find((m) => m.id === id);
        if (target) target.status = status;
      }),

    allocateEchoID: () => {
      let id = '';
      set((state) => {
        state.echoIDCounter += 1;
        id = `${state.sessionID ?? 0}-u${state.echoIDCounter}`;
      });
      return id;
    },

    captureSwap: (activeEnvelope, visibleSystemMessages) => {
      // Read via `get()` (not inside the `set` producer below) so `lastSeenEnvelope` is the
      // real, already-committed object — not an Immer draft Proxy that only exists for the
      // lifetime of a producer. Comparing a draft Proxy against `activeEnvelope` (a plain,
      // non-draft object) is NEVER reference-equal even when they represent the identical
      // object, which would make this check permanently true and archive every single render.
      // Same pitfall, same fix, as `_clearStaleResolved`'s guard in hil-react's hilStore.ts —
      // see its comment for the full explanation.
      const { lastSeenEnvelope, lastSeenVisible } = get();
      const shouldCapture =
        lastSeenEnvelope !== null && lastSeenEnvelope !== activeEnvelope && lastSeenVisible.length > 0;

      set((state) => {
        if (shouldCapture) {
          const echoedBoundary = state.echoed.length;
          for (const m of state.lastSeenVisible) {
            state.historyIDCounter += 1;
            state.systemHistory.push({
              message: { ...m, id: `${state.sessionID ?? 0}-sys${state.historyIDCounter}` },
              echoedBoundary,
            });
          }
        }
        state.lastSeenEnvelope = activeEnvelope;
        state.lastSeenVisible = visibleSystemMessages;
      });
    },
  }))
);
